/**
 * Plays the music files in `/music` (see trackNames.ts for the naming). The synthesised score is
 * the fallback wherever there is no file, so this never has to succeed: `play` says whether it did.
 * Two audio elements, so one track fades out under the next.
 */

import { pickTrack, trackTable } from './trackNames';

const FILES = import.meta.glob('/music/*.{mp3,ogg,m4a,wav,flac}', { query: '?url', import: 'default', eager: true });

const FADE_SECONDS = 1.4;
const STEP_MS = 50;

export class FileMusic {
  private readonly table = trackTable(FILES as Record<string, string>);
  private current: HTMLAudioElement | null = null;
  private currentName: string | null = null;
  private readonly fading = new Set<HTMLAudioElement>();
  private volume = 0.5;
  private timer = 0;
  /** A browser refuses to start audio before the first key or click: remembered and started then. */
  private blocked = false;

  /** Every track found, for the curious (`window.__music`). */
  names(): string[] {
    return [...this.table.keys()].sort();
  }

  get playing(): string | null {
    return this.currentName;
  }

  /** Seconds into the current track: above zero means it really is sounding (for checks). */
  get position(): number {
    return this.current?.currentTime ?? 0;
  }

  /**
   * Fade to the first of `names` that exists. False (and silence from here) if none does.
   * `once`: a short piece that plays through and stops, rather than looping.
   */
  play(names: readonly string[], once = false): boolean {
    const track = pickTrack(this.table, names);
    if (!track) {
      this.stop();
      return false;
    }
    if (track.name === this.currentName) return true;
    this.retire();
    const el = new Audio(track.url);
    el.loop = !once;
    // A piece that has played out is no longer playing: the next request for it starts it again.
    el.addEventListener('ended', () => {
      if (el !== this.current) return;
      this.current = null;
      this.currentName = null;
    });
    el.volume = 0;
    this.current = el;
    this.currentName = track.name;
    this.start(el);
    this.run();
    return true;
  }

  stop(): void {
    this.retire();
    this.run();
  }

  /** 0..1, already including the master slider. */
  setVolume(volume: number): void {
    this.volume = Math.min(1, Math.max(0, volume));
    this.run();
  }

  /** Call from any key or click: starts what the browser would not let start earlier. */
  unlock(): void {
    if (this.blocked && this.current) this.start(this.current);
  }

  private start(el: HTMLAudioElement): void {
    this.blocked = false;
    el.play().catch(() => {
      if (el === this.current) this.blocked = true;
    });
  }

  private retire(): void {
    if (this.current) this.fading.add(this.current);
    this.current = null;
    this.currentName = null;
  }

  /** One fade loop for everything: the current track toward the volume, retired ones toward silence. */
  private run(): void {
    if (this.timer !== 0) return;
    const step = STEP_MS / 1000 / FADE_SECONDS;
    this.timer = window.setInterval(() => {
      let busy = false;
      const el = this.current;
      if (el && Math.abs(el.volume - this.volume) > 0.001) {
        el.volume = el.volume < this.volume ? Math.min(this.volume, el.volume + step) : Math.max(this.volume, el.volume - step);
        busy = true;
      }
      for (const old of this.fading) {
        old.volume = Math.max(0, old.volume - step);
        if (old.volume <= 0.001) {
          old.pause();
          old.removeAttribute('src');
          this.fading.delete(old);
        } else busy = true;
      }
      if (!busy) {
        window.clearInterval(this.timer);
        this.timer = 0;
      }
    }, STEP_MS);
  }
}
